import { CoinCounterPill, TopCounterLayout, formatCoinCount } from "../../common_ui_elements/coin_counter_pill.js";
import { Vector2 } from "../../../common/vector2.js";
import { ScreenManager, Screens } from "../../../screen_manager/screen_manager.js";
import { formatMinutesShort } from "../../../common/reward_format.js";
import { Game } from "../../../game.js";
import { ImagesHolder } from "../../../image_loader/images_holder.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { StatisticsStorage } from "../../../storage/statistics_storage.js";
import { BasicEventsController } from "../../../touch_events_controller/basic_events_controller.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { RoundedRectangle } from "../../common_ui_elements/rounded_rectangle.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UIElement } from "../../common_ui_elements/ui_element.js";
import { UIElementHolder } from "../../common_ui_elements/ui_element_holder.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../../common_ui_elements/ui_nine_slice_element.js";
import { UIPatternCanvas } from "../../common_ui_elements/ui_pattern_canvas.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { UITileImage } from "../../common_ui_elements/ui_tile_image.js";
import { UIScrollableContainer } from "../../common_ui_elements/ui_scrollable_container.js";
import { SafeAreas } from "../../safe_areas.js";
import { OfferService } from "../../../offers/offer_service.js";
import { formatShortDuration } from "../../../common/common_funcs.js";
import { LocalizationManager } from "../../../localization/localization_manager.js";
import { IapPlugin, type IapProductDetails } from "../../../plugins/iap_plugin.js";
import { adoptEconomyFromPlayer, refreshPlayerFromServer } from "../../../api/player_sync.js";
import { logVirtualCurrencyEarned } from "../../../analytics/appsflyer_events.js";
import { OfferPlacement } from "../../../api/dto/offers.dto.js";
import type { ClaimFreeOfferResponse, OfferDto, OfferRewardDto, OfferShopSection, ValidatePurchaseResponse } from "../../../api/dto/offers.dto.js";
import { audioController } from "../../../audio/audio_controller.js";
import { AudioClips } from "../../../audio/audio_constants.js";
import { OfferTriggerController } from "../../../offers/offer_trigger_controller.js";
import { offerButtonLabel, resolveBundleVisual, resolveDealVisual } from "../../../offers/offer_visual_registry.js";
import { showRewarded } from "../../../ads/rewarded.js";
import { crashCapture } from "../../../crash/crash_reporter.js";
import { RewardTooltip } from "../../tooltip/reward_tooltip.js";
import { hidePurchaseFailedTooltip, showPurchaseFailedTooltip } from "../../tooltip/purchase_failed_tooltip.js";
import { isPurchaseErrorReported } from "../../../offers/purchase_failure.js";
import { trackFeatureEvent } from "../../../analytics/feature_events.js";

type OfferPriceButtonLayout = {
  button_width: number;
  button_height: number;
  text_weight: number;
  shadow_y: number;
  shadow_blur: number;
};

type TimedOfferVisual = {
  ends_at_ms: number;
  text: UITextElement;
  hand: UIImage;
};

type OfferPriceLoadedCallback = ( details: IapProductDetails ) => void;

export class ShopScreen extends UIElement
{
  /** Green shop button → purple: the FREE state until dedicated art lands. */
  private static readonly FREE_BUTTON_HUE_ROTATION = 160;
  private events_controller: BasicEventsController | null = null;
  private content_scroll!: UIScrollableContainer;
  private content_holder!: UIElement;
  private purchase_in_progress_offer_id: string | null = null;
  private coins_pill: CoinCounterPill | null = null;
  private next_coins_sync_at = 0;
  private section_buttons_holder?: UIElementHolder;
  private active_section_index = 0;
  private section_offsets_ready = false;
  private section_scroll_offsets: number[] = [];
  private visible_shop_sections: OfferShopSection[] = [];
  private readonly product_details_cache = new Map<string, Promise<IapProductDetails | null>>();
  private readonly shown_offer_ids = new Set<string>();
  private timed_offer_visuals: TimedOfferVisual[] = [];
  /** Grey price buttons counting down to the next purchase cycle. */
  private locked_offer_buttons: Array<{ text: UITextElement; resets_at_ms: number }> = [];
  private locked_offer_tooltip: UIElementHolder | null = null;
  private locked_offer_dismiss_layer: UIElementHolder | null = null;
  private locked_offer_tooltip_timeout: number | null = null;
  private next_timed_offer_update_at = 0;
  private timed_offer_refresh_in_progress = false;
  private offer_shop_opened_reported = false;
  private offer_shop_closed_reported = false;
  private shop_shown_at = 0;
  private offers_key = "";

  public onCoinsPurchased: ( () => void ) | null = null;
  /** Off when embedded in an overlay: there the ctx is still offset by the
   * parent after finishRender(), so the host draws the strip itself. */
  public render_top_safe_area = true;

  private static formatCoins( value: number | string ): string
  {
    return formatCoinCount( value );
  }

  constructor( ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, own_input = true )
  {
    super( ctx, x, y, width, height, false, false );

    if ( own_input )
      this.initializeEventsController( ctx.canvas );

    this.createBackground();
    this.createContentHolder();
    this.createBottomBackground();
    this.createTopBlock();
    void this.refreshOffers();
  }

  private createBackground(): void
  {
    const bg_x = -Game.safeAreaLeft();
    const bg_y = -Game.safeAreaTop();
    const bg_w = this.rect.getW() + Game.safeAreaLeft() + Game.safeAreaRight();
    const bg_h = this.rect.getH() + Game.safeAreaTop() + Game.safeAreaBottom();

    const bg_color = new RoundedRectangle(
      this.ctx,
      bg_x,
      bg_y,
      bg_w,
      bg_h,
      0,
      "#173A98"
    )

    this.addChild( bg_color );

    const bg = new UIPatternCanvas( this.ctx, bg_x, bg_y, bg_w, bg_h, {
      pattern_image: ImagesHolder.getImage( ImgID.UI_ShopTileBg ),
      // background_color: "#173A98",
      tile_width: LayoutScaler.widthFromDesign( 132 ),
      tile_height: LayoutScaler.heightFromDesign( 162 ),
      // tile_opacity: 0.6
    } );

    this.addChild( bg );
  }

  private createTopBlock(): void
  {
    const w = this.rect.getW() + Game.safeAreaLeft() + Game.safeAreaRight();
    const h = LayoutScaler.heightFromDesign( 339 ) + Game.safeAreaTop();
    const x = -Game.safeAreaLeft();
    const y = -Game.safeAreaTop();

    const bg = new UINineSliceElement(
      this.ctx,
      x,
      y,
      w,
      h,
      ImgID.UI_ShopTopBg,
      "shop_top_bg"
    );

    this.addChild( bg );

    // XD: Y 353, H 288. Its mockup width is intentionally ignored: the
    // horizontal nine-slice always covers the complete screen and safe areas.
    const tabs_bg = new UINineSliceElement(
      this.ctx,
      x,
      LayoutScaler.yFromDesign( 353 ),
      w,
      LayoutScaler.heightFromDesign( 288 ),
      ImgID.UI_ShopTabsBg,
      "shop_tabs_bg"
    );
    this.addChild( tabs_bg );

    // XD (1080x1920): title component X 391, Y 220, W 304, H 99 — moved down to
    // Y 240: the coin counter now sits on the menu's spot (Y 122, not the XD 99)
    // and its coin otherwise touched the title.
    const title_w = LayoutScaler.widthFromDesign( 304 );
    const title_h = LayoutScaler.heightFromDesign( 99 );
    const title_x = w / 2 - title_w / 2;
    const title_y = Game.safeAreaTop() + LayoutScaler.yFromDesign( 240 );
    const title_font_size = LayoutScaler.textSizeFromDesign( 70 );
    const title_outer_outline = LayoutScaler.textBorderSizeFromDesign( 24 );
    const title_inner_outline = LayoutScaler.textBorderSizeFromDesign( 8 );
    const title_shadow_offset = LayoutScaler.yFromDesign( 8 );
    // Four-color title from the mockup: a solid extruded shadow, a broad
    // golden rim, then the cream face with its narrow chocolate outline.
    const title_shadow = this.createTitleLayer(
      title_x,
      title_y + title_shadow_offset,
      title_w,
      title_h,
      title_font_size,
      ColorScheme.shop_title_shadow,
      title_outer_outline,
      ColorScheme.shop_title_shadow
    );
    const title_outer_rim = this.createTitleLayer(
      title_x,
      title_y,
      title_w,
      title_h,
      title_font_size,
      ColorScheme.shop_title_outer_outline,
      title_outer_outline,
      ColorScheme.shop_title_outer_outline
    );
    const title_face = this.createTitleLayer(
      title_x,
      title_y,
      title_w,
      title_h,
      title_font_size,
      ColorScheme.shop_title_text,
      title_inner_outline,
      ColorScheme.shop_title_inner_outline
    );
    bg.addChild( title_shadow );
    bg.addChild( title_outer_rim );
    bg.addChild( title_face );

    // The same spot as the menu's counter (and the dialogs' copy of it), so the
    // balance does not jump when switching tabs or opening the coin shop overlay.
    const coin_x = TopCounterLayout.coinX();
    const coin_y = TopCounterLayout.y();

    this.coins_pill = new CoinCounterPill( this.ctx, coin_x, coin_y, { plus: false } );
    this.addChild( this.coins_pill );

    const tile_image_x = LayoutScaler.xFromDesign( -212 );
    const tile_image_y = LayoutScaler.yFromDesign( 339 );
    const tile_image_w = this.rect.getW() - tile_image_x;
    const tile_image_h = LayoutScaler.heightFromDesign( 114 );

    const tile_image = new UITileImage(
      this.ctx,
      tile_image_x,
      tile_image_y,
      tile_image_w,
      tile_image_h,
      ImgID.UI_ShopTopImg,
      LayoutScaler.widthFromDesign( 261 ),
    )
    tile_image.setRepeatStep( LayoutScaler.widthFromDesign( 252 ) );

    this.addChild( tile_image );
    this.createSectionNavigation();
  }

  private createTitleLayer(
    x: number,
    y: number,
    width: number,
    height: number,
    font_size: number,
    text_color: string,
    outline_width: number,
    outline_color: string
  ): UITextElement
  {
    return new UITextElement(
      this.ctx,
      x,
      y,
      width,
      height,
      {
        localization_key: "SHOP",
        font_size,
        font_weight: 900,
        text_color,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Top,
        outline_width,
        outline_color,
        auto_wrap: false
      }
    );
  }

  private createSectionNavigation(): void
  {
    const tab_y = LayoutScaler.yFromDesign( 489 );
    const tab_h = LayoutScaler.heightFromDesign( 108 );
    this.section_buttons_holder = new UIElementHolder(
      this.ctx,
      0,
      tab_y,
      this.rect.getW(),
      tab_h,
      false,
      false
    );
    this.addChild( this.section_buttons_holder );
    this.populateSectionButtons();
  }

  private populateSectionButtons(): void
  {
    if ( !this.section_buttons_holder ) return;
    this.section_buttons_holder.removeAllChildren();

    const tab_keys: Record<OfferShopSection, string> = {
      bundles: "SHOP.TAB.BUNDLES",
      gold: "SHOP.TAB.GOLD",
      deals: "SHOP.TAB.DEALS",
    };
    const tab_positions: Record<number, number[]> = {
      1: [ 383 ],
      2: [ 218, 548 ],
      3: [ 52, 383, 714 ],
    };
    const visible_sections = this.visible_shop_sections;
    const tab_x = tab_positions[visible_sections.length] ?? [];

    const slot_w = 314;
    const slot_h = 108;
    const tabs_offset_x = ( this.rect.getW() - LayoutScaler.widthFromDesign( 1080 ) ) / 2;

    visible_sections.forEach( ( section, index ) =>
    {
      const localization_key = tab_keys[section];
      const is_active = index === this.active_section_index;
      const art_w = is_active ? 330 : 327;
      const art_h = 130;
      const tab_w = LayoutScaler.widthFromDesign( art_w );
      const tab_h = LayoutScaler.heightFromDesign( art_h );
      const tab_x_px = tabs_offset_x + LayoutScaler.xFromDesign( tab_x[index] + ( slot_w - art_w ) / 2 );
      const tab_y_px = LayoutScaler.yFromDesign( ( slot_h - art_h ) / 2 );
      const tab = new UIImage(
        this.ctx,
        tab_x_px,
        tab_y_px,
        tab_w,
        tab_h,
        is_active ? ImgID.UI_ShopTabActive : ImgID.UI_ShopTabInactive,
        {},
        true
      );
      tab.setOnClick( () =>
      {
        void audioController.playSound( AudioClips.UI_CLICK );
        this.scrollToSection( index );
      } );
      this.section_buttons_holder!.addChild( tab );

      const label = new UITextElement(
        this.ctx,
        0,
        0,
        tab_w,
        tab_h,
        {
          localization_key,
          font_size: LayoutScaler.textSizeFromDesign( 45 ),
          font_weight: 900,
          text_color: ColorScheme.white,
          text_style: TextStyleScheme.geologica,
          text_align: MyTextAlign.Center,
          vertical_align: MyTextVerticalAlign.Center,
          outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
          outline_color: ColorScheme.shop_tab_text_outline,
          outline_offset_y: LayoutScaler.yFromDesign( 1 ),
          shadow_color: ColorScheme.black_transparent,
          shadow_blur: LayoutScaler.widthFromDesign( 8 ),
          shadow_offset_y: LayoutScaler.yFromDesign( 2 ),
          auto_wrap: false,
          scale_to_width: true,
          min_font_size: LayoutScaler.textSizeFromDesign( 32 )
        }
      );
      tab.addChild( label, true );
    } );
  }

  private scrollToSection( section_index: number ): void
  {
    if ( section_index < 0 || section_index >= this.section_scroll_offsets.length ) return;
    this.active_section_index = section_index;
    this.populateSectionButtons();
    // The first section starts below the content's top padding: scrolling to its offset
    // stopped short of the top and left that padding (and what overhangs the card) hidden.
    const offset = section_index === 0 ? 0 : this.section_scroll_offsets[section_index];
    this.content_scroll.scrollTo( offset, true );
  }

  private syncActiveSectionToScroll(): void
  {
    if ( !this.section_offsets_ready ) return;

    // Switch at the midpoint between neighbouring section starts. This keeps
    // the first tab active at scroll=0 while still switching before the next
    // section has consumed the entire viewport.
    const scroll_offset = this.content_scroll.getScrollOffset();
    let section_index = 0;
    for ( let index = 1; index < this.section_scroll_offsets.length; index++ )
    {
      const activation_offset = ( this.section_scroll_offsets[index - 1] + this.section_scroll_offsets[index] ) * 0.5;
      if ( scroll_offset >= activation_offset ) section_index = index;
    }

    if ( section_index === this.active_section_index ) return;
    this.active_section_index = section_index;
    this.populateSectionButtons();
  }

  private createContentHolder(): void
  {
    const scroll_y = LayoutScaler.yFromDesign( 641 );
    // Cards continue behind the permanent bottom gradient and the navigation.
    const scroll_h = Math.max( 1, this.rect.getH() - scroll_y );

    this.content_scroll = new UIScrollableContainer(
      this.ctx,
      0,
      scroll_y,
      this.rect.getW(),
      scroll_h,
      {
        direction: "vertical",
        scroll_speed: 1,
        enable_momentum: true,
        enable_touch_scroll: true,
        scroll_sensitivity: 0.9,
        enable_rubber_band: true,
        rubber_band_resistance: 0.45,
        rubber_band_max_distance: LayoutScaler.heightFromDesign( 80 ),
        rubber_band_progression: 3
      }
    );

    this.content_holder = new UIElementHolder(
      this.ctx,
      0,
      0,
      this.rect.getW(),
      scroll_h,
      false,
      false
    );
    this.content_scroll.addScrollableChild( this.content_holder );
    this.content_scroll.setContentHeight( scroll_h );
    this.content_scroll.setOnScrollEnd( () =>
    {
      this.syncActiveSectionToScroll();
    } );
    this.addChild( this.content_scroll );
  }

  private createBottomBackground(): void
  {
    const h = LayoutScaler.heightFromDesign( 352 );
    const y = Math.max( 0, this.rect.getH() - h );
    const bottom_bg = new UINineSliceElement(
      this.ctx,
      0,
      y,
      this.rect.getW(),
      h,
      ImgID.UI_ShopBottomBg,
      "shop_bottom_bg"
    );
    // This is a visual mask between the scroll and the fixed navigation. It
    // must never intercept a drag that starts over the lower part of the list.
    bottom_bg.setClickable( false );
    this.addChild( bottom_bg );
  }

  private getOfferNumberData( offer: OfferDto, keys: string[] ): number
  {
    const data = offer.data ?? {};
    const raw = keys.map( key => data[key] ).find( value => value !== undefined );
    const value = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number( raw ) : 0;
    return Number.isFinite( value ) ? Math.max( 0, Math.floor( value ) ) : 0;
  }

  private getCoinAmount( offer: OfferDto ): number
  {
    const configured = this.getRewards( offer ).find( reward => reward.type === 'coins' )?.amount ?? 0;
    if ( configured > 0 ) return configured;
    const from_data = this.getOfferNumberData( offer, [ 'coins', 'coin', 'amount', 'count' ] );
    if ( from_data > 0 ) return from_data;

    const product_id = offer.products.android?.productId ?? offer.products.ios?.productId ?? "";
    const match = `${offer.id} ${offer.entitlement_key} ${product_id}`.match( /coin(?:s)?[_-]?(\d+)/i );
    return match ? Number( match[1] ) : 0;
  }

  private getRewards( offer: OfferDto ): OfferRewardDto[]
  {
    const raw = offer.rewards ?? offer.data?.rewards;
    if ( Array.isArray( raw ) ) return raw.filter( ( value ): value is OfferRewardDto =>
      Boolean( value ) && typeof value === 'object' && typeof ( value as OfferRewardDto ).type === 'string'
    );
    if ( !raw || typeof raw !== 'object' ) return [];
    return Object.entries( raw ).flatMap( ( [ type, amount ] ) => {
      if ( type === 'no_ads' && amount === true ) return [{ type: 'no_ads' as const, amount: 1 }];
      return typeof amount === 'number' && amount > 0 ? [{ type: type as OfferRewardDto['type'], amount }] : [];
    } );
  }

  private getPrimaryReward( offer: OfferDto ): OfferRewardDto | null
  {
    return this.getRewards( offer ).find( reward => reward.type !== 'no_ads' ) ?? null;
  }

  private grantsNoAds( offer: OfferDto ): boolean
  {
    return this.getRewards( offer ).some( reward => reward.type === 'no_ads' );
  }

  private rewardIcon( type: OfferRewardDto['type'] ): ImgID
  {
    switch ( type )
    {
      case 'coins': return ImgID.IconCoin;
      case 'hearts': return ImgID.IconHeart;
      case 'infinite_hearts_minutes': return ImgID.UI_ShopInfiniteHeart;
      case 'no_ads_minutes': return ImgID.UI_RemoveAdsIcon;
      case 'level_shots': return ImgID.UI_DailyTasksTaskCannon;
      case 'big_balls': return ImgID.UI_GameStartBigBallBooster;
      case 'infinite_balls': return ImgID.UI_GameStartInfiniteBallsBooster;
      case 'multi_balls': return ImgID.UI_GameStartMultiballsBooster;
      case 'fireworks': return ImgID.UI_GameStartFireworkBooster;
      case 'mines': return ImgID.UI_PassiveBoosterBomb;
      case 'no_ads': return ImgID.UI_RemoveAdsIcon;
    }
  }

  private isShopOffer( offer: OfferDto ): boolean
  {
    const data = offer.data ?? {};
    if ( offer.kind && offer.kind !== 'static_shop' ) return false;
    if ( offer.id === "no_ads" || offer.entitlement_key === "no_ads" ) return true;
    if ( data.show_in_shop === true || data.shop === true ) return true;
    if ( data.placement === OfferPlacement.Shop ) return true;
    if ( Array.isArray( data.placements ) && data.placements.includes( OfferPlacement.Shop ) ) return true;

    return this.getRewards( offer ).length > 0 || this.getCoinAmount( offer ) > 0;
  }

  private async refreshOffers(): Promise<void>
  {
    if ( OfferService.getOffers().length > 0 )
    {
      this.createContent();
      await OfferService.refresh();
      if ( this.offers_key !== this.computeOffersKey() )
        this.createContent();
    }
    else
    {
      this.renderMessage( "Loading..." );
      await OfferService.refresh();
      this.createContent();
    }
    if ( !this.offer_shop_opened_reported && ScreenManager.getCurrentScreen() === Screens.SHOP )
    {
      this.offer_shop_opened_reported = true;
      if ( this.shop_shown_at <= 0 ) this.shop_shown_at = Date.now();
      void OfferTriggerController.report( 'shop_opened' );
    }
  }

  private renderMessage( text_value: string ): void
  {
    this.content_holder.removeAllChildren();
    const content_h = this.content_scroll.getRect().getH();
    this.content_holder.getRect().resize( this.rect.getW(), content_h );
    this.content_scroll.setContentHeight( content_h );
    const message = new UITextElement(
      this.ctx,
      0,
      content_h * 0.5,
      this.rect.getW(),
      LayoutScaler.heightFromDesign( 100 ),
      {
        text: text_value,
        text_color: ColorScheme.white,
        font_size: LayoutScaler.textSizeFromDesign( 52 ),
        font_weight: 700,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
      }
    );
    this.content_holder.addChild( message );
  }

  private createContent(): void
  {
    this.hideLockedOfferTooltip();
    this.content_holder.removeAllChildren();
    this.timed_offer_visuals = [];
    this.locked_offer_buttons = [];
    this.next_timed_offer_update_at = 0;
    // An offer whose cycle limit is spent stays on the shelf as a locked card
    // with a countdown (the server only keeps ones that will reset); a
    // lifetime cap is filtered server-side.
    const offers = OfferService.getOffers()
      .filter( offer => offer.enabled && this.isShopOffer( offer ) )
      .sort( ( a, b ) => b.priority - a.priority );

    this.offers_key = this.computeOffersKey();
    this.createShopContent( offers );
  }

  private computeOffersKey(): string
  {
    const offers = OfferService.getOffers()
      .filter( offer => offer.enabled && this.isShopOffer( offer ) )
      .map( offer => [ offer.id, offer.priority, OfferService.getPurchaseLimitState( offer ) ] );
    return JSON.stringify( [ StatisticsStorage.load().level, offers ] );
  }

  private async refreshOffersOnReturn(): Promise<void>
  {
    await OfferService.refresh();
    if ( this.offers_key !== this.computeOffersKey() )
      this.createContent();
    if ( !this.offer_shop_opened_reported )
    {
      this.offer_shop_opened_reported = true;
      if ( this.shop_shown_at <= 0 ) this.shop_shown_at = Date.now();
      void OfferTriggerController.report( 'shop_opened' );
    }
  }

  public canReuseScreen(): boolean
  {
    return this.offers_key !== "";
  }

  public onScreenHidden(): void
  {
    this.reportShopClosed();
    this.hideLockedOfferTooltip();
    hidePurchaseFailedTooltip();
    if ( this.offer_shop_opened_reported && !this.offer_shop_closed_reported )
    {
      this.offer_shop_closed_reported = true;
      void OfferTriggerController.report( 'shop_closed' );
    }
    this.events_controller?.disable();
  }

  public onScreenShown(): void
  {
    this.events_controller?.enable();
    this.offer_shop_opened_reported = false;
    this.offer_shop_closed_reported = false;
    this.shop_shown_at = Date.now();
    this.content_scroll.scrollTo( 0, false );
    this.syncCoins();
    void this.refreshOffersOnReturn();
  }

  private getScrollableContentHeight( content_bottom: number ): number
  {
    const viewport_h = this.content_scroll.getRect().getH();
    const bottom_clearance = LayoutScaler.heightFromDesign( 352 );
    return Math.max( viewport_h, content_bottom + bottom_clearance );
  }

  /** The server sends this explicitly. The fallback keeps old published
   * catalogs usable until they are opened and saved once in the new editor. */
  private getShopSection( offer: OfferDto ): OfferShopSection
  {
    const data = offer.data ?? {};
    const configured = offer.shop_section ?? data.shop_section ?? data.shopSection;
    if ( configured === 'bundles' || configured === 'gold' || configured === 'deals' ) return configured;
    if ( offer.id === 'no_ads' || offer.entitlement_key === 'no_ads' ) return 'bundles';
    if ( data.bundle === true || data.shop_tab === 'bundles' || data.category === 'bundles' ) return 'bundles';
    if ( /bundle|set/i.test( `${offer.id} ${offer.entitlement_key}` ) ) return 'bundles';
    if ( /quick|promo/i.test( `${offer.id} ${offer.entitlement_key} ${String( data.category ?? '' )}` ) ) return 'deals';
    return 'gold';
  }

  private trackShopOfferShown( offer: OfferDto ): void
  {
    if ( this.shown_offer_ids.has( offer.id ) ) return;
    this.shown_offer_ids.add( offer.id );
    void OfferService.trackShown( offer.id, this.shopAnalyticsContext( offer ) );
  }

  /** Placement plus the card state a player actually saw (locked / free / rewarded). */
  private shopAnalyticsContext( offer: OfferDto, extra: Record<string, unknown> = {} ): Record<string, unknown>
  {
    const limit_state = OfferService.getPurchaseLimitState( offer );
    const locked = Boolean( limit_state && limit_state.left <= 0 && limit_state.resets_at_ms !== null );
    return {
      placement: OfferPlacement.Shop,
      placement_id: OfferPlacement.Shop,
      shop_section: this.getShopSection( offer ),
      ...( locked ? { locked: true } : {} ),
      ...( OfferService.isRewardedOffer( offer.id ) ? { rewarded: true } : {} ),
      ...extra,
    };
  }

  private createShopContent( offers: OfferDto[] ): void
  {
    // Card art, title and ribbon come from the offer's configured visual;
    // offers without one fall back to the old position-based styling inside
    // the registry.
    const bundle_offers = offers.filter( offer => this.getShopSection( offer ) === 'bundles' );
    const bundles = bundle_offers.map( ( offer, index ) => ( {
      visual: resolveBundleVisual( offer, index, this.grantsNoAds( offer ) ),
      offer,
    } ) );
    const coin_offers = offers.filter( offer => this.getShopSection( offer ) === 'gold' );
    const deal_offers = offers.filter( offer => this.getShopSection( offer ) === 'deals' );
    const card_h = LayoutScaler.heightFromDesign( 520 );
    const gap = LayoutScaler.heightFromDesign( 20 );
    const start_y = LayoutScaler.yFromDesign( 14 );
    const coin_block_h = LayoutScaler.heightFromDesign( 190 );
    const timer_overflow = LayoutScaler.heightFromDesign( 34 );
    const vertical_h = LayoutScaler.heightFromDesign( 1152 );
    const content_offset_x = ( this.rect.getW() - LayoutScaler.widthFromDesign( 1080 ) ) / 2;
    const section_offsets: Partial<Record<OfferShopSection, number>> = {};
    let next_y = start_y;

    if ( bundles.length )
    {
      section_offsets.bundles = next_y;
      bundles.forEach( ( bundle, index ) =>
      {
        const offer = bundle.offer;
        const card_y = next_y + index * ( card_h + gap );
        if ( bundle.visual.kind === "no_ads" ) this.createNoAdsCard( card_y, offer, bundle.visual.title_key, bundle.visual.description_key );
        else this.createBundleCard(
          card_y,
          this.getBundleCoinAmount( offer ),
          bundle.visual.title_key,
          bundle.visual.coin_image,
          bundle.visual.best,
          offer
        );
        this.trackShopOfferShown( offer );
      } );
      next_y += bundles.length * card_h + ( bundles.length - 1 ) * gap + LayoutScaler.heightFromDesign( 40 );
    }

    if ( coin_offers.length )
    {
      section_offsets.gold = next_y;
      coin_offers.forEach( ( offer, index ) =>
      {
        this.createItems( next_y + index * coin_block_h, offer );
        this.trackShopOfferShown( offer );
      } );
      next_y += coin_offers.length * coin_block_h + LayoutScaler.heightFromDesign( 40 );
    }

    if ( deal_offers.length )
    {
      next_y += timer_overflow;
      section_offsets.deals = next_y;
      deal_offers.forEach( ( offer, index ) =>
      {
        const row = Math.floor( index / 2 );
        const column = index % 2;
        const visual = resolveDealVisual( offer, column );
        this.createVerticalOfferCard(
          content_offset_x + LayoutScaler.xFromDesign( column === 0 ? 40 : 552 ),
          next_y + row * ( vertical_h + gap ),
          visual.title_key,
          offer,
          visual.promotional
        );
        this.trackShopOfferShown( offer );
      } );
      next_y += Math.ceil( deal_offers.length / 2 ) * vertical_h
        + ( Math.ceil( deal_offers.length / 2 ) - 1 ) * gap;
    }

    const previous_active = this.visible_shop_sections[this.active_section_index];
    this.visible_shop_sections = ([ 'bundles', 'gold', 'deals' ] as OfferShopSection[])
      .filter( section => section_offsets[section] !== undefined );
    this.active_section_index = Math.max( 0, this.visible_shop_sections.indexOf( previous_active ) );
    this.section_scroll_offsets = this.visible_shop_sections.map( section => section_offsets[section] ?? 0 );
    this.section_offsets_ready = this.section_scroll_offsets.length > 0;
    this.populateSectionButtons();

    this.content_holder.getRect().resize( this.rect.getW(), this.getScrollableContentHeight( next_y ) );
    this.content_scroll.setContentHeight( this.getScrollableContentHeight( next_y ) );
  }

  private createBundleCard(
    y: number,
    amount: number | null,
    title_key: string,
    coin_image_id: ImgID,
    is_best: boolean,
    offer?: OfferDto
  ): void
  {
    const w = LayoutScaler.widthFromDesign( 1008 );
    const h = LayoutScaler.heightFromDesign( 520 );
    const design_content_w = LayoutScaler.widthFromDesign( 1080 );
    const x = ( this.rect.getW() - design_content_w ) / 2 + LayoutScaler.xFromDesign( 40 );
    const card = new UIImage( this.ctx, x, y, w, h, ImgID.UI_ShopBundleBg );
    this.content_holder.addChild( card );

    const coins = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( 48 ),
      LayoutScaler.yFromDesign( 49 ),
      LayoutScaler.widthFromDesign( 368 ),
      LayoutScaler.heightFromDesign( 275 ),
      coin_image_id
    );
    card.addChild( coins );

    // The coin artwork is a permanent part of the bundle card. Its amount is
    // only shown when coins are an explicit reward, never as a stand-in for a
    // different reward in the bundle.
    if ( amount !== null )
    {
      const amount_text = new UITextElement(
        this.ctx,
        LayoutScaler.xFromDesign( 139 ),
        LayoutScaler.yFromDesign( 222 ),
        LayoutScaler.widthFromDesign( 230 ),
        LayoutScaler.heightFromDesign( 98 ),
        {
          text: ShopScreen.formatCoins( amount ),
          font_size: LayoutScaler.textSizeFromDesign( 70 ),
          font_weight: 900,
          text_color: ColorScheme.white,
          text_style: TextStyleScheme.geologica,
          text_align: MyTextAlign.Center,
          vertical_align: MyTextVerticalAlign.Center,
          outline_width: LayoutScaler.textBorderSizeFromDesign( 14 ),
          outline_color: "#003F87",
          auto_wrap: false,
          scale_to_width: true,
          min_font_size: LayoutScaler.textSizeFromDesign( 55 ),
        }
      );
      card.addChild( amount_text );
      RewardTooltip.attach( coins, "coins", [ amount_text ] );
    }

    if ( is_best )
    {
      const best_badge = new UIImage(
        this.ctx,
        LayoutScaler.xFromDesign( -31.04 ),
        LayoutScaler.yFromDesign( -36.65 ),
        LayoutScaler.widthFromDesign( 218 ),
        LayoutScaler.heightFromDesign( 218 ),
        ImgID.UI_ShopBestBadge,
        { rotation_angle: -23 }
      );
      card.addChild( best_badge );
      const best_text = new UITextElement(
        this.ctx,
        LayoutScaler.xFromDesign( 34.46 ),
        LayoutScaler.yFromDesign( 39.35 ),
        LayoutScaler.widthFromDesign( 87 ),
        LayoutScaler.heightFromDesign( 54 ),
        {
          text: "Best",
          font_size: LayoutScaler.textSizeFromDesign( 40 ),
          font_weight: 900,
          text_color: "#FFFFFF",
          text_style: TextStyleScheme.geologica,
          text_align: MyTextAlign.Center,
          vertical_align: MyTextVerticalAlign.Center,
          outline_width: 0,
          auto_wrap: false,
        }
      );
      best_text.enableRotation();
      best_text.setRotationImmediate( -23 * Math.PI / 180 );
      card.addChild( best_text );
    }

    this.addBundleRewards( card, offer );

    const title = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( 51 ),
      LayoutScaler.yFromDesign( 372 ),
      LayoutScaler.widthFromDesign( 470 ),
      LayoutScaler.heightFromDesign( 96 ),
      {
        localization_key: title_key,
        font_size: LayoutScaler.textSizeFromDesign( 60 ),
        font_weight: 900,
        text_color: "#1E5285",
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Left,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 14 ),
        outline_color: ColorScheme.white,
        shadow_blur: 0,
        shadow_offset_x: 0,
        shadow_offset_y: 0,
        outline_shadow_blur: 0,
        outline_shadow_offset_x: 0,
        outline_shadow_offset_y: 0,
        auto_wrap: false,
        scale_to_width: true,
        min_font_size: LayoutScaler.textSizeFromDesign( 48 )
      }
    );
    card.addChild( title );

    this.addOfferPriceButton(
      card,
      w - LayoutScaler.widthFromDesign( 456 ) - LayoutScaler.xFromDesign( 20 ),
      LayoutScaler.yFromDesign( 330 ),
      offer,
      undefined,
      true
    );
  }

  private addBundleHeartReward( card: UIElement ): void
  {
    const icon_x = LayoutScaler.xFromDesign( 561.5 );
    const text_x = LayoutScaler.xFromDesign( 561.5 );
    const y = LayoutScaler.yFromDesign( 21 );
    const size = LayoutScaler.widthFromDesign( 132 );
    const heart_icon = new UIImage( this.ctx, icon_x, y, size, size, ImgID.UI_ShopInfiniteHeart );
    const duration_text = new UITextElement(
      this.ctx,
      text_x,
      LayoutScaler.yFromDesign( 110 ),
      size,
      LayoutScaler.heightFromDesign( 62 ),
      {
        text: "1h",
        font_size: LayoutScaler.textSizeFromDesign( 45 ),
        font_weight: 900,
        text_color: "#FFFFFF",
        text_style: TextStyleScheme.geologica,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
        outline_color: "#003F87",
        shadow_blur: LayoutScaler.widthFromDesign( 2 ),
        shadow_offset_y: LayoutScaler.yFromDesign( 4 ),
        shadow_color: "#002C6B",
        auto_wrap: false
      }
    );
    card.addChild( heart_icon );
    card.addChild( duration_text );
  }

  private addBundleNoAdsReward( card: UIElement ): void
  {
    const icon_x = LayoutScaler.xFromDesign( 699.5 );
    const text_x = LayoutScaler.xFromDesign( 699.5 );
    const y = LayoutScaler.yFromDesign( 37 );
    const size = LayoutScaler.widthFromDesign( 121 );
    const no_ads_icon = new UIImage(
      this.ctx,
      icon_x,
      y,
      size,
      size,
      ImgID.UI_RemoveAdsIcon
    );
    const duration_text = new UITextElement(
      this.ctx,
      text_x,
      LayoutScaler.yFromDesign( 110 ),
      size,
      LayoutScaler.heightFromDesign( 62 ),
      {
        text: "1h",
        font_size: LayoutScaler.textSizeFromDesign( 45 ),
        font_weight: 900,
        text_color: "#FFFFFF",
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
        outline_color: "#003F87",
        shadow_blur: LayoutScaler.widthFromDesign( 2 ),
        shadow_offset_y: LayoutScaler.yFromDesign( 4 ),
        shadow_color: "#002C6B",
        auto_wrap: false
      }
    );
    card.addChild( no_ads_icon );
    card.addChild( duration_text );
  }

  private addBundleBoosterReward( card: UIElement, x: number, image_id: ImgID ): void
  {
    const icon_x = x + LayoutScaler.xFromDesign( 5 );
    const icon_size = LayoutScaler.widthFromDesign( 112 );
    const booster_icon = new UIImage(
      this.ctx,
      icon_x,
      LayoutScaler.yFromDesign( 181 ),
      icon_size,
      icon_size,
      image_id
    );
    const amount_text = new UITextElement(
      this.ctx,
      icon_x,
      LayoutScaler.yFromDesign( 260 ),
      icon_size,
      LayoutScaler.heightFromDesign( 62 ),
      {
        text: "×3",
        font_size: LayoutScaler.textSizeFromDesign( 45 ),
        font_weight: 900,
        text_color: "#FFFFFF",
        text_style: TextStyleScheme.geologica,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
        outline_color: "#003F87",
        shadow_blur: LayoutScaler.widthFromDesign( 2 ),
        shadow_offset_y: LayoutScaler.yFromDesign( 4 ),
        shadow_color: "#002C6B",
        auto_wrap: false
      }
    );
    card.addChild( booster_icon );
    card.addChild( amount_text );
  }

  /** Renders exactly the non-coin items configured for this bundle. */
  private addBundleRewards( card: UIElement, offer?: OfferDto ): void
  {
    if ( !offer ) return;

    const rewards = this.getRewards( offer )
      .filter( reward => reward.type !== 'coins' )
      .slice( 0, 8 );
    const positions = this.getBundleRewardPositions( rewards.length );

    rewards.forEach( ( reward, index ) =>
    {
      const position = positions[index];
      const icon_size = LayoutScaler.widthFromDesign( 112 );
      const icon_x = LayoutScaler.xFromDesign( position.x + 5 );
      const icon_y = LayoutScaler.yFromDesign( position.y );
      const label_width = LayoutScaler.widthFromDesign( 124 );
      const label_x = icon_x - ( label_width - icon_size ) / 2;
      const icon = new UIImage( this.ctx, icon_x, icon_y, icon_size, icon_size, this.rewardIcon( reward.type ) );
      card.addChild( icon );

      const amount = this.formatBundleRewardAmount( reward );
      if ( !amount )
      {
        RewardTooltip.attach( icon, reward.type );
        return;
      }
      const label = new UITextElement(
        this.ctx,
        label_x,
        icon_y + LayoutScaler.yFromDesign( 79 ),
        label_width,
        LayoutScaler.heightFromDesign( 62 ),
        {
          text: amount,
          font_size: LayoutScaler.textSizeFromDesign( 45 ),
          font_weight: 900,
          text_color: "#FFFFFF",
          text_style: TextStyleScheme.geologica,
          text_align: MyTextAlign.Center,
          vertical_align: MyTextVerticalAlign.Center,
          outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
          outline_color: "#003F87",
          shadow_blur: LayoutScaler.widthFromDesign( 2 ),
          shadow_offset_y: LayoutScaler.yFromDesign( 4 ),
          shadow_color: "#002C6B",
          auto_wrap: false,
          scale_to_width: true,
          min_font_size: LayoutScaler.textSizeFromDesign( 32 ),
        }
      );
      card.addChild( label );
      RewardTooltip.attach( icon, reward.type, [ label ] );
    } );
  }

  /**
   * Slots occupy the right side of the card while the large coin artwork stays
   * on the left. Rows are centred independently, matching the 1–8 item card
   * layouts: 1–4, then 2/3, 3/3, 3/4 and 4/4.
   */
  private getBundleRewardPositions( count: number ): Array<{ x: number; y: number }>
  {
    const grid_offset_x = 35;
    const row = ( slots: number, y: number ) => Array.from(
      { length: slots },
      ( _, index ) => ( {
        x: 434 + grid_offset_x + ( 4 - slots ) * 66.25 + index * 132.5,
        y,
      } )
    );

    if ( count <= 0 ) return [];
    // A single reward row must align with the visual centre of the large coin
    // stack, rather than with the lower row used by two-row layouts.
    if ( count <= 4 ) return row( count, 141 );

    const [ top_count, bottom_count ] = count === 5 ? [ 2, 3 ]
      : count === 6 ? [ 3, 3 ]
      : count === 7 ? [ 3, 4 ]
      : [ 4, 4 ];
    return [ ...row( top_count, 21 ), ...row( bottom_count, 181 ) ];
  }

  private getBundleCoinAmount( offer: OfferDto ): number | null
  {
    const amount = this.getRewards( offer ).find( reward => reward.type === 'coins' )?.amount;
    return typeof amount === 'number' && amount > 0 ? amount : null;
  }

  private formatBundleRewardAmount( reward: OfferRewardDto ): string
  {
    if ( reward.type === 'no_ads' ) return '';
    if ( reward.type === 'no_ads_minutes' ) return formatMinutesShort( reward.amount );
    if ( reward.type === 'infinite_hearts_minutes' )
    {
      const hours = Math.floor( reward.amount / 60 );
      const minutes = reward.amount % 60;
      const h = LocalizationManager.t( "TIME.SHORT_HOURS" );
      const m = LocalizationManager.t( "TIME.SHORT_MINUTES" );
      return hours && minutes ? `${hours}${h} ${minutes}${m}` : hours ? `${hours}${h}` : `${minutes}${m}`;
    }
    return `x${ShopScreen.formatCoins( reward.amount )}`;
  }

  private createNoAdsCard( y: number, offer?: OfferDto, title_key = "REMOVE.ADS", description_key = "REMOVE.ADS.TEXT" ): void
  {
    const w = LayoutScaler.widthFromDesign( 1008 );
    const h = LayoutScaler.heightFromDesign( 520 );
    const x = ( this.rect.getW() - LayoutScaler.widthFromDesign( 1080 ) ) / 2
      + LayoutScaler.xFromDesign( 40 );
    const card = new UIImage( this.ctx, x, y, w, h, ImgID.UI_ShopNoAdsBundleBg );
    this.content_holder.addChild( card );

    const remove_ads_icon = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( 62 ),
      LayoutScaler.yFromDesign( 60 ),
      LayoutScaler.widthFromDesign( 274 ),
      LayoutScaler.heightFromDesign( 274 ),
      ImgID.UI_RemoveAdsIcon
    );
    RewardTooltip.attach( remove_ads_icon, "no_ads" );
    card.addChild( remove_ads_icon );

    const description = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( 350 ),
      LayoutScaler.yFromDesign( 86 ),
      LayoutScaler.widthFromDesign( 620 ),
      LayoutScaler.heightFromDesign( 220 ),
      {
        localization_key: description_key,
        font_size: LayoutScaler.textSizeFromDesign( 52 ),
        font_weight: 900,
        text_color: "#FFFFFF",
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 12 ),
        outline_color: "#113E95",
        line_height: 1,
        auto_wrap: true,
      }
    );
    card.addChild( description );

    const title = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( 45 ),
      LayoutScaler.yFromDesign( 350 ),
      LayoutScaler.widthFromDesign( 470 ),
      LayoutScaler.heightFromDesign( 130 ),
      {
        localization_key: title_key,
        font_size: LayoutScaler.textSizeFromDesign( 54 ),
        font_weight: 900,
        text_color: "#FF456A",
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 12 ),
        outline_color: ColorScheme.white,
        shadow_blur: 0,
        shadow_offset_x: 0,
        shadow_offset_y: 0,
        outline_shadow_blur: 0,
        outline_shadow_offset_x: 0,
        outline_shadow_offset_y: 0,
        line_height: 1,
        auto_wrap: true,
      }
    );
    card.addChild( title );

    this.addOfferPriceButton(
      card,
      w - LayoutScaler.widthFromDesign( 456 ) - LayoutScaler.xFromDesign( 20 ),
      LayoutScaler.yFromDesign( 330 ),
      offer,
      undefined,
      true
    );
  }

  private createVerticalOfferCard(
    x: number,
    y: number,
    title_key: string,
    offer: OfferDto | undefined,
    promotional: boolean
  ): void
  {
    const w = LayoutScaler.widthFromDesign( 488 );
    const h = LayoutScaler.heightFromDesign( 1152 );
    const card = new UIElementHolder( this.ctx, x, y, w, h, false, false );
    this.content_holder.addChild( card );

    const background = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( -5 ),
      LayoutScaler.yFromDesign( -6 ),
      LayoutScaler.widthFromDesign( 498 ),
      LayoutScaler.heightFromDesign( 1164 ),
      ImgID.UI_ShopDiscountBg
    );
    card.addChild( background );
    const ends_at_ms = this.getTimedOfferEndMs( offer );
    if ( ends_at_ms !== null ) this.addVerticalOfferTimer( card, ends_at_ms );

    const title = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( 45 ),
      LayoutScaler.yFromDesign( 66 ),
      LayoutScaler.widthFromDesign( 398 ),
      LayoutScaler.heightFromDesign( 105 ),
      {
        localization_key: title_key,
        font_size: LayoutScaler.textSizeFromDesign( 66 ),
        font_weight: 900,
        text_color: "#1E5285",
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 14 ),
        outline_color: ColorScheme.white,
        shadow_blur: 0,
        shadow_offset_x: 0,
        shadow_offset_y: 0,
        outline_shadow_blur: 0,
        outline_shadow_offset_x: 0,
        outline_shadow_offset_y: 0,
        line_height: 0.9,
        auto_wrap: !promotional,
        scale_to_width: true,
        min_font_size: LayoutScaler.textSizeFromDesign( 40 ),
      }
    );
    card.addChild( title );

    const coins = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( 53.98 ),
      LayoutScaler.yFromDesign( 176.58 ),
      LayoutScaler.widthFromDesign( 368 ),
      LayoutScaler.heightFromDesign( 261 ),
      promotional ? ImgID.UI_ShopBundleCoinsLarge : ImgID.UI_ShopBundleCoinsSmallSet
    );
    card.addChild( coins );
    const coin_amount = offer ? this.getBundleCoinAmount( offer ) : null;
    if ( coin_amount !== null )
    {
      const coins_amount = new UITextElement(
        this.ctx,
        LayoutScaler.xFromDesign( 102.98 ),
        LayoutScaler.yFromDesign( 353.58 ),
        LayoutScaler.widthFromDesign( 272 ),
        LayoutScaler.heightFromDesign( 98 ),
        {
          text: ShopScreen.formatCoins( coin_amount ),
          font_size: LayoutScaler.textSizeFromDesign( 70 ),
          font_weight: 900,
          text_color: ColorScheme.white,
          text_style: TextStyleScheme.geologica,
          text_align: MyTextAlign.Center,
          vertical_align: MyTextVerticalAlign.Center,
          outline_width: LayoutScaler.textBorderSizeFromDesign( 14 ),
          outline_color: "#003F87",
          auto_wrap: false,
        }
      );
      card.addChild( coins_amount );
      RewardTooltip.attach( coins, "coins", [ coins_amount ] );
    }

    const reward_count = this.addVerticalOfferRewards( card, offer );
    const discount_y = reward_count >= 7 ? 830 : reward_count >= 5 ? 820 : 812.83;
    const discount_percent = this.getOfferDiscountPercent( offer );

    this.addOfferPriceButton(
      card,
      LayoutScaler.xFromDesign( 18.51 ),
      LayoutScaler.yFromDesign( 956.94 ),
      offer,
      {
        button_width: 455.07,
        button_height: 169,
        text_weight: 800,
        shadow_y: 3,
        shadow_blur: 6,
      },
      true,
      discount_percent === null
        ? undefined
        : details => this.addVerticalDiscountPrice( card, discount_y, discount_percent, details )
    );
  }

  private getTimedOfferEndMs( offer: OfferDto | undefined ): number | null
  {
    return offer ? OfferService.getOfferDisplayEndMs( offer ) : null;
  }

  private addVerticalOfferTimer( card: UIElement, ends_at_ms: number ): void
  {
    const timer_w = LayoutScaler.widthFromDesign( 320 );
    const timer_h = LayoutScaler.heightFromDesign( 96 );
    const timer_x = ( card.getRect().getW() - timer_w ) / 2;
    const timer_y = LayoutScaler.yFromDesign( -34 );
    const timer_center_y = timer_y + timer_h / 2;
    const timer_bg = new UIImage(
      this.ctx,
      timer_x,
      timer_y,
      timer_w,
      timer_h,
      ImgID.UI_ShopTimerBg
    );
    card.addChild( timer_bg );

    const clock_size = LayoutScaler.widthFromDesign( 55.51 );
    const clock = new UIImage(
      this.ctx,
      timer_x + LayoutScaler.xFromDesign( 20 ),
      timer_center_y - clock_size / 2,
      clock_size,
      clock_size,
      ImgID.UI_ClockFrame
    );
    card.addChild( clock );

    const hand_h = clock_size * 0.8;
    const hand_w = hand_h * ( 24 / 73 );
    const hand = new UIImage(
      this.ctx,
      ( clock_size - hand_w ) / 2,
      ( clock_size - hand_h ) / 2,
      hand_w,
      hand_h,
      ImgID.UI_ClockHand
    );
    hand.enableRotation();
    hand.setRotationImmediate( 30 * Math.PI / 180 );
    clock.addChild( hand );

    const text_h = LayoutScaler.heightFromDesign( 66 );
    const text = new UITextElement(
      this.ctx,
      timer_x + LayoutScaler.xFromDesign( 88 ),
      timer_center_y - text_h / 2,
      LayoutScaler.widthFromDesign( 214 ),
      text_h,
      {
        text: "…",
        font_size: LayoutScaler.textSizeFromDesign( 48 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
        scale_to_width: true,
        min_font_size: LayoutScaler.textSizeFromDesign( 32 ),
      }
    );
    card.addChild( text );
    this.timed_offer_visuals.push( { ends_at_ms, text, hand } );
    this.updateTimedOfferVisuals( true );
  }

  private formatTimedOfferRemaining( remaining_ms: number ): string
  {
    return formatShortDuration( remaining_ms );
  }

  /**
   * "Come back in: 19г 11хв" on the same tape the menu uses for locked events,
   * anchored above the tapped button. Goes away on any tap or after a moment.
   */
  private showLockedOfferTooltip( button: UIElement, resets_at_ms: number ): void
  {
    this.hideLockedOfferTooltip();

    const clamp = ( value: number, min: number, max: number ) => Math.min( Math.max( value, min ), max );
    const root_rect = this.getAbsoluteRect();
    const button_rect = button.getAbsoluteRect();
    const button_x = button_rect.x - root_rect.x;
    const button_y = button_rect.y - root_rect.y;

    const tape_w = LayoutScaler.widthFromDesign( 675 );
    const tape_h = LayoutScaler.heightFromDesign( 200 );
    const bottom_w = LayoutScaler.widthFromDesign( 110.46 );
    const bottom_h = LayoutScaler.heightFromDesign( 77.12 );
    const side_margin = LayoutScaler.widthFromDesign( 33 );
    const button_center_x = button_x + button_rect.width / 2;

    const tape_x = clamp( button_center_x - tape_w / 2, side_margin, this.rect.getW() - side_margin - tape_w );
    const tape_y = button_y - LayoutScaler.heightFromDesign( 219.75 );
    const bottom_x = clamp( button_center_x - tape_x - bottom_w / 2, 0, tape_w - bottom_w );
    const bottom_y = LayoutScaler.heightFromDesign( 167.68 );

    const tooltip = new UIElementHolder( this.ctx, tape_x, tape_y, tape_w, Math.max( tape_h, bottom_y + bottom_h ) );
    tooltip.setClickable( false );

    const dismiss_layer = new UIElementHolder( this.ctx, 0, 0, this.rect.getW(), this.rect.getH() );
    dismiss_layer.setOnClick( () => this.hideLockedOfferTooltip(), true );

    const tape = new UINineSliceElement( this.ctx, 0, 0, tape_w, tape_h, ImgID.UI_GameBoosterLockedTape, "booster_tape" );
    tooltip.addChild( tape );

    const now = OfferService.getServerNowMs() ?? Date.now();
    const text = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( 30 ),
      LayoutScaler.yFromDesign( 20 ),
      tape_w - LayoutScaler.widthFromDesign( 60 ),
      tape_h - LayoutScaler.heightFromDesign( 40 ),
      {
        text: LocalizationManager.tWithParams( "SHOP.OFFER.LOCKED_UNTIL", [ this.formatTimedOfferRemaining( Math.max( 0, resets_at_ms - now ) ) ] ),
        font_size: LayoutScaler.textSizeFromDesign( 52 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
        outline_color: ColorScheme.continue_overlay_caption_outline,
        fit_font_size: true,
        min_font_size: LayoutScaler.textSizeFromDesign( 30 ),
        line_height: 0.95,
        auto_wrap: true,
      }
    );
    tape.addChild( text );

    const bottom = new UIImage( this.ctx, bottom_x, bottom_y, bottom_w, bottom_h, ImgID.UI_GameBoosterBottom );
    tooltip.addChild( bottom );

    this.locked_offer_dismiss_layer = dismiss_layer;
    this.addChild( dismiss_layer );
    this.locked_offer_tooltip = tooltip;
    this.addChild( tooltip );
    this.locked_offer_tooltip_timeout = window.setTimeout( () => this.hideLockedOfferTooltip(), 3000 );
    this.invalidateSubtree();
  }

  private hideLockedOfferTooltip(): void
  {
    if ( this.locked_offer_tooltip_timeout !== null )
    {
      window.clearTimeout( this.locked_offer_tooltip_timeout );
      this.locked_offer_tooltip_timeout = null;
    }
    if ( !this.locked_offer_tooltip && !this.locked_offer_dismiss_layer ) return;

    if ( this.locked_offer_tooltip ) this.removeChild( this.locked_offer_tooltip );
    if ( this.locked_offer_dismiss_layer ) this.removeChild( this.locked_offer_dismiss_layer );
    this.locked_offer_tooltip = null;
    this.locked_offer_dismiss_layer = null;
    this.invalidateSubtree();
  }

  private updateTimedOfferVisuals( force: boolean = false ): void
  {
    // The server remains authoritative when it validates a purchase. A client
    // that did not receive server_time can still show an already supplied
    // absolute deadline instead of silently hiding the timer.
    const now = OfferService.getServerNowMs() ?? Date.now();
    if ( !force && now < this.next_timed_offer_update_at ) return;
    this.next_timed_offer_update_at = now + 1_000;

    let expired = false;
    for ( const visual of this.timed_offer_visuals )
    {
      const remaining_ms = Math.max( 0, visual.ends_at_ms - now );
      this.setTextAndRedraw( visual.text, this.formatTimedOfferRemaining( remaining_ms ) );
      const minute_progress = ( remaining_ms / 60_000 ) % 60;
      visual.hand.setRotationImmediate( ( minute_progress / 60 ) * 2 * Math.PI );
      if ( remaining_ms === 0 ) expired = true;
    }
    for ( const button of this.locked_offer_buttons )
    {
      const remaining_ms = Math.max( 0, button.resets_at_ms - now );
      this.setTextAndRedraw( button.text, this.formatTimedOfferRemaining( remaining_ms ) );
      // The cycle is over: refetch so the card comes back green with a price.
      if ( remaining_ms === 0 ) expired = true;
    }

    if ( expired && !this.timed_offer_refresh_in_progress )
    {
      this.timed_offer_refresh_in_progress = true;
      void this.refreshOffers().finally( () => { this.timed_offer_refresh_in_progress = false; } );
    }
  }

  /** Renders the configured non-coin deal rewards in a compact three-column grid. */
  private addVerticalOfferRewards( card: UIElement, offer?: OfferDto ): number
  {
    if ( !offer ) return 0;

    const rewards = this.getRewards( offer )
      .filter( reward => reward.type !== 'coins' )
      .slice( 0, 8 );
    const slots = this.getVerticalOfferRewardSlots( rewards );

    rewards.forEach( ( reward, index ) =>
    {
      const slot = slots[index];
      const icon_size = LayoutScaler.widthFromDesign( slot.icon_size );
      const label_width = LayoutScaler.widthFromDesign( slot.label_width );
      const icon_x = LayoutScaler.xFromDesign( slot.x + ( slot.label_width - slot.icon_size ) / 2 );
      const icon_y = LayoutScaler.yFromDesign( slot.y );
      const icon = new UIImage( this.ctx, icon_x, icon_y, icon_size, icon_size, this.rewardIcon( reward.type ) );
      card.addChild( icon );

      const amount = this.formatBundleRewardAmount( reward );
      if ( !amount )
      {
        RewardTooltip.attach( icon, reward.type );
        return;
      }
      const label = new UITextElement(
        this.ctx,
        LayoutScaler.xFromDesign( slot.x ),
        icon_y + LayoutScaler.yFromDesign( slot.label_offset ),
        label_width,
        LayoutScaler.heightFromDesign( slot.label_height ),
        {
          text: amount,
          font_size: LayoutScaler.textSizeFromDesign( slot.font_size ),
          font_weight: 900,
          text_color: "#FFFFFF",
          text_style: TextStyleScheme.geologica,
          text_align: MyTextAlign.Center,
          vertical_align: MyTextVerticalAlign.Center,
          outline_width: LayoutScaler.textBorderSizeFromDesign( slot.outline_width ),
          outline_color: "#003F87",
          auto_wrap: false,
          scale_to_width: true,
          min_font_size: LayoutScaler.textSizeFromDesign( Math.max( 24, slot.font_size - 10 ) ),
        }
      );
      card.addChild( label );
      RewardTooltip.attach( icon, reward.type, [ label ] );
    } );

    return rewards.length;
  }

  private getVerticalOfferRewardSlots( rewards: OfferRewardDto[] ): Array<{
    x: number;
    y: number;
    icon_size: number;
    label_width: number;
    label_offset: number;
    label_height: number;
    font_size: number;
    outline_width: number;
  }>
  {
    const count = rewards.length;
    if ( count <= 0 ) return [];

    const compact = count >= 7;
    const icon_size = compact ? 72 : 120;
    const label_width = compact ? 92 : 120;
    const label_offset = compact ? 55 : 92;
    const label_height = compact ? 42 : 58;
    const font_size = compact ? 34 : 42;
    const outline_width = compact ? 7 : 9;
    const columns = Math.min( 3, count );
    const row_gap = compact ? 100 : 150;
    const rows = Math.ceil( count / columns );
    const start_y = rows === 1 ? 565 : 496;
    const slots = [];

    for ( let row = 0; row < rows; row++ )
    {
      const column_count = Math.min( columns, count - row * columns );
      const gap = compact ? 35 : 30;
      const start_x = ( 488 - ( column_count * label_width + ( column_count - 1 ) * gap ) ) / 2;
      for ( let column = 0; column < column_count; column++ )
      {
        slots.push( {
          x: start_x + column * ( label_width + gap ),
          y: start_y + row * row_gap,
          icon_size,
          label_width,
          label_offset,
          label_height,
          font_size,
          outline_width,
        } );
      }
    }

    // The reward PNGs have very different transparent side padding. Centre
    // every row by its visible artwork, not by the equally sized image boxes.
    for ( let row = 0; row < rows; row++ )
    {
      const row_start = row * columns;
      const row_end = Math.min( row_start + columns, count );
      let visible_left = Number.POSITIVE_INFINITY;
      let visible_right = Number.NEGATIVE_INFINITY;

      for ( let index = row_start; index < row_end; index++ )
      {
        const slot = slots[index];
        const bounds = this.rewardVisibleHorizontalBounds( rewards[index].type );
        const image_x = slot.x + ( slot.label_width - slot.icon_size ) / 2;
        visible_left = Math.min( visible_left, image_x + slot.icon_size * bounds.left );
        visible_right = Math.max( visible_right, image_x + slot.icon_size * bounds.right );
      }

      const row_offset = 488 / 2 - ( visible_left + visible_right ) / 2;
      for ( let index = row_start; index < row_end; index++ )
        slots[index].x += row_offset;
    }

    return slots;
  }

  private rewardVisibleHorizontalBounds( type: OfferRewardDto['type'] ): { left: number; right: number }
  {
    switch ( type )
    {
      case 'hearts': return { left: 17 / 192, right: 173 / 192 };
      case 'infinite_hearts_minutes': return { left: 13 / 121, right: 107 / 121 };
      case 'big_balls': return { left: 29 / 256, right: 229 / 256 };
      case 'infinite_balls': return { left: 50 / 256, right: 206 / 256 };
      case 'multi_balls': return { left: 14 / 256, right: 239 / 256 };
      case 'fireworks': return { left: 39 / 256, right: 235 / 256 };
      case 'mines': return { left: 20 / 257, right: 243 / 257 };
      default: return { left: 0, right: 1 };
    }
  }

  private addOfferPriceButton(
    parent: UIElement,
    x: number,
    y: number,
    offer?: OfferDto,
    layout?: OfferPriceButtonLayout,
    enable_purchase = false,
    on_price_loaded?: OfferPriceLoadedCallback
  ): void
  {
    const w = LayoutScaler.widthFromDesign( layout?.button_width ?? 456 );
    const h = LayoutScaler.heightFromDesign( layout?.button_height ?? 169 );
    const limit_state = offer ? OfferService.getPurchaseLimitState( offer ) : null;
    const locked_until_ms = limit_state && limit_state.left <= 0 ? limit_state.resets_at_ms : null;
    const rewarded = Boolean( offer && locked_until_ms === null && OfferService.isRewardedOffer( offer.id ) );
    // The rewarded state borrows the chain screen's purple video button; it
    // keeps its own aspect so the film icon is not stretched.
    const btn_h = rewarded ? w * ( 133 / 435 ) : h;
    const btn = new UIImage( this.ctx, x, y + ( h - btn_h ) / 2, w, btn_h, rewarded ? ImgID.UI_ChainOfferVideoIcon : ImgID.UI_ShopBtn, {}, true );
    parent.addChild( btn );
    // One artwork, three states: green to buy, purple for a free claim, grey
    // while the purchase cycle is spent (the label then counts down instead
    // of showing a price).
    if ( locked_until_ms !== null ) btn.setDimmed( true );
    else if ( offer && OfferService.isFreeOffer( offer.id ) ) btn.setHueRotation( ShopScreen.FREE_BUTTON_HUE_ROTATION );
    // The film icon sits in the left ~22% of the video button; the label takes the rest.
    const label_x = rewarded ? w * 0.22 : 0;

    const outline_color = locked_until_ms !== null ? ColorScheme.non_active_btn_outline
      : rewarded ? ColorScheme.more_lives_purple_outline : ColorScheme.more_lives_buy_outline;
    const price_text = new UITextElement(
      this.ctx,
      label_x,
      0,
      w - label_x,
      btn_h,
      {
        text: "Buy",
        font_size: LayoutScaler.textSizeFromDesign( 60 ),
        font_weight: layout?.text_weight ?? 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
        scale_to_width: true,
        min_font_size: LayoutScaler.textSizeFromDesign( 35 ),
        outline_color,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
        outline_offset_y: LayoutScaler.yFromDesign( 2 ),
        ...TextStyleScheme.buttonShadow(),
        ...( layout ? {
          shadow_blur: LayoutScaler.widthFromDesign( layout.shadow_blur ),
          shadow_offset_x: 0,
          shadow_offset_y: LayoutScaler.yFromDesign( layout.shadow_y ),
        } : {} ),
      }
    );
    btn.addChild( price_text, true );
    if ( locked_until_ms !== null )
    {
      // Not purchasable, but a tap still explains why instead of doing nothing.
      btn.setOnClick( () =>
      {
        void audioController.playSound( AudioClips.UI_CLICK );
        if ( offer ) void OfferService.trackClicked( offer.id, this.shopAnalyticsContext( offer, { locked: true } ) );
        this.showLockedOfferTooltip( btn, locked_until_ms );
      } );
      this.locked_offer_buttons.push( { text: price_text, resets_at_ms: locked_until_ms } );
      this.updateTimedOfferVisuals( true );
      return;
    }
    btn.setOnClick( () =>
    {
      void audioController.playSound( AudioClips.UI_CLICK );
      if ( enable_purchase && offer )
        void this.purchaseOffer( offer, price_text, btn );
    } );
    if ( enable_purchase && offer )
      void this.loadPrice( offer, price_text, on_price_loaded );
  }

  private createItems( y: number, offer: OfferDto ): void
  {
    const reward = this.getPrimaryReward( offer );
    const count = reward?.amount ?? this.getCoinAmount( offer );
    const is_coin_reward = !reward || reward.type === 'coins';
    const w = LayoutScaler.widthFromDesign( 1020 );
    const h = LayoutScaler.heightFromDesign( 190 );
    const x = this.rect.getW() / 2 - w / 2;

    const card = new UIElementHolder(
      this.ctx,
      x,
      y,
      w,
      h,
      false,
      false
    );
    this.content_holder.addChild( card );

    const background = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( -5 ),
      LayoutScaler.yFromDesign( -5 ),
      LayoutScaler.widthFromDesign( 1030 ),
      LayoutScaler.heightFromDesign( 200 ),
      ImgID.UI_ShopCoinOfferBg
    );
    card.addChild( background );

    const img_w = LayoutScaler.widthFromDesign( 204 );
    const img_h = LayoutScaler.heightFromDesign( 155 );
    const img_x = LayoutScaler.xFromDesign( 20 );
    const img_y = h / 2 - img_h / 2;

    const img = new UIImage(
      this.ctx,
      img_x,
      img_y,
      img_w,
      img_h,
      is_coin_reward ? ImgID.UI_ShopBlockImg : this.rewardIcon( reward.type ),
      {}
    );

    RewardTooltip.attach( img, is_coin_reward ? "coins" : reward.type );
    card.addChild( img );

    const count_text_w = LayoutScaler.widthFromDesign( 300 );
    const count_text_h = LayoutScaler.heightFromDesign( 100 );
    const count_text_x = LayoutScaler.xFromDesign( 235 );
    const count_text_y = h / 2 - count_text_h / 2;

    const count_text = new UITextElement(
      this.ctx,
      count_text_x,
      count_text_y,
      count_text_w,
      count_text_h,
      {
        text: reward?.type === 'infinite_hearts_minutes'
          ? `${count >= 60 && count % 60 === 0 ? count / 60 : count}${count >= 60 && count % 60 === 0 ? 'h' : 'm'}`
          : reward?.type === 'no_ads' ? 'NO ADS'
            : reward?.type === 'no_ads_minutes' ? formatMinutesShort( count )
              : ShopScreen.formatCoins( count ),
        vertical_align: MyTextVerticalAlign.Center,
        text_align: MyTextAlign.Left,
        text_color: ColorScheme.white,
        font_size: LayoutScaler.textSizeFromDesign( 70 ),
        font_weight: 900,
        text_style: TextStyleScheme.geologica,
        auto_wrap: false,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 14 ),
        outline_color: ColorScheme.shop_block_count,
        outline_offset_y: LayoutScaler.yFromDesign( 2 ),
      }
    );
    card.addChild( count_text );

    this.addOfferPriceButton(
      card,
      w - LayoutScaler.widthFromDesign( 456 ) - LayoutScaler.xFromDesign( 18 ),
      ( h - LayoutScaler.heightFromDesign( 169 ) ) / 2 - LayoutScaler.yFromDesign( 6 ),
      offer,
      undefined,
      true
    );
  }

  private getOfferDiscountPercent( offer: OfferDto | undefined ): number | null
  {
    if ( !offer ) return null;
    const raw = offer.discount_percent ?? offer.data?.discount_percent ?? offer.data?.discountPercent;
    const percent = typeof raw === 'number' ? raw : Number( raw );
    return Number.isInteger( percent ) && percent >= 1 && percent <= 99 ? percent : null;
  }

  /** Adds the saving label only after the live store price is available. */
  private addVerticalDiscountPrice(
    card: UIElement,
    discount_y: number,
    discount_percent: number,
    details: IapProductDetails
  ): void
  {
    const price_micros = Number( details.price_micros );
    if ( !Number.isFinite( price_micros ) || price_micros <= 0 || !details.currency ) return;

    const original_price = price_micros / 1_000_000 / ( 1 - discount_percent / 100 );
    let formatted_original_price: string;
    try
    {
      formatted_original_price = new Intl.NumberFormat( undefined, {
        style: 'currency',
        currency: details.currency,
        maximumFractionDigits: 2,
      } ).format( original_price );
    }
    catch
    {
      formatted_original_price = `${original_price.toFixed( 2 )} ${details.currency}`;
    }

    const discount = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( 130.89 ),
      LayoutScaler.yFromDesign( discount_y ),
      LayoutScaler.widthFromDesign( 248 ),
      LayoutScaler.heightFromDesign( 54 ),
      {
        localization_key: "SHOP.OFFER.DISCOUNT",
        text_variable_params: [ String( discount_percent ) ],
        font_size: LayoutScaler.textSizeFromDesign( 40 ),
        font_weight: 800,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
        scale_to_width: true,
        min_font_size: LayoutScaler.textSizeFromDesign( 30 ),
        shadow_blur: LayoutScaler.widthFromDesign( 6 ),
        shadow_color: ColorScheme.black_transparent,
        shadow_offset_x: 0,
        shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
      }
    );
    card.addChild( discount );

    const old_price = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( 135.04 ),
      LayoutScaler.yFromDesign( 901.94 ),
      LayoutScaler.widthFromDesign( 222 ),
      LayoutScaler.heightFromDesign( 54 ),
      {
        text: formatted_original_price,
        font_size: LayoutScaler.textSizeFromDesign( 40 ),
        font_weight: 800,
        text_color: "#652323",
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
        scale_to_width: true,
        min_font_size: LayoutScaler.textSizeFromDesign( 30 ),
      }
    );
    card.addChild( old_price );

    const strike = new RoundedRectangle(
      this.ctx,
      LayoutScaler.xFromDesign( 136.04 ),
      LayoutScaler.yFromDesign( 925.44 ),
      LayoutScaler.widthFromDesign( 220 ),
      LayoutScaler.heightFromDesign( 7 ),
      LayoutScaler.widthFromDesign( 3.5 ),
      "#FF0000"
    );
    strike.enableRotation();
    strike.setRotationImmediate( -10 * Math.PI / 180 );
    card.addChild( strike );
    this.invalidateSubtree();
  }

  private async loadPrice(
    offer: OfferDto,
    price_text: UITextElement,
    on_price_loaded?: OfferPriceLoadedCallback
  ): Promise<void>
  {
    if ( OfferService.isFreeOffer( offer.id ) || OfferService.isRewardedOffer( offer.id ) )
    {
      this.setTextAndRedraw( price_text, offerButtonLabel( offer, "FREE" ) );
      return;
    }
    const product_id = OfferService.getPlatformProductId( offer.id );
    if ( !product_id )
    {
      this.setTextAndRedraw( price_text, "N/A" );
      return;
    }

    try
    {
      const details = await this.getProductDetails( product_id );
      this.setTextAndRedraw( price_text, details?.formatted_price ?? "Buy" );
      if ( details ) on_price_loaded?.( details );
      const price_context = {
        placement: OfferPlacement.Shop,
        productid: product_id,
        formatted_price: details?.formatted_price,
        currency: details?.currency,
        price_micros: details?.price_micros,
      };
      if ( details?.formatted_price ) await OfferService.trackPriceLoaded( offer.id, price_context );
      else await OfferService.trackPriceUnavailable( offer.id, { ...price_context, failure_reason: 'product_details_missing' } );
    }
    catch ( e )
    {
      console.warn( '[Shop] price load failed', e );
      this.setTextAndRedraw( price_text, "Buy" );
      await OfferService.trackPriceUnavailable( offer.id, { placement: OfferPlacement.Shop, productid: product_id } );
    }
  }

  private getProductDetails( product_id: string ): Promise<IapProductDetails | null>
  {
    const cached = this.product_details_cache.get( product_id );
    if ( cached ) return cached;

    const request = IapPlugin.getProductDetails( product_id ).catch( e =>
    {
      this.product_details_cache.delete( product_id );
      throw e;
    } );
    this.product_details_cache.set( product_id, request );
    return request;
  }

  private setTextAndRedraw( text_element: UITextElement, text: string ): void
  {
    text_element.setText( text );
    this.invalidateSubtree();
  }

  private async applyPurchaseEconomy( validation: Pick<ValidatePurchaseResponse | ClaimFreeOfferResponse, 'player'> ): Promise<void>
  {
    const coins_before = StatisticsStorage.load().coins;
    if ( validation.player )
    {
      adoptEconomyFromPlayer( validation.player );
    }
    else
    {
      await refreshPlayerFromServer();
    }

    const coins_earned = Math.max( 0, StatisticsStorage.load().coins - coins_before );
    void logVirtualCurrencyEarned( {
      currency_name: 'coins',
      amount: coins_earned,
      source: 'iap_purchase',
    } );

    this.syncCoins();
  }

  private syncCoins(): void
  {
    if ( this.coins_pill?.syncCoins() )
      this.invalidateSubtree();
  }

  private async purchaseOffer( offer: OfferDto, price_text: UITextElement, button: UIElement ): Promise<void>
  {
    if ( this.purchase_in_progress_offer_id ) return;
    hidePurchaseFailedTooltip();

    this.purchase_in_progress_offer_id = offer.id;
    const previous_text = price_text.getText();
    this.setTextAndRedraw( price_text, "..." );

    try
    {
      await OfferService.trackClicked( offer.id, this.shopAnalyticsContext( offer ) );
      let validation: ValidatePurchaseResponse | ClaimFreeOfferResponse;
      if ( OfferService.isRewardedOffer( offer.id ) )
      {
        // The grant is only claimed after the SDK confirms the video was
        // watched to the end; a skipped or unavailable ad just restores the button.
        const watched = await showRewarded( 'shop_offer' );
        await OfferService.trackRewardedAdOutcome( offer.id, watched, this.shopAnalyticsContext( offer ) );
        if ( !watched.rewarded )
        {
          this.setTextAndRedraw( price_text, previous_text );
          return;
        }
        validation = await OfferService.claimFreeOffer( offer.id, this.shopAnalyticsContext( offer, { rewarded_ad_id: watched.ad_id } ), { ad_id: watched.ad_id } );
      }
      else
      {
        validation = OfferService.isFreeOffer( offer.id )
          ? await OfferService.claimFreeOffer( offer.id, this.shopAnalyticsContext( offer ) )
          : await OfferService.purchaseOffer( offer.id, this.shopAnalyticsContext( offer ) );
      }
      await this.applyPurchaseEconomy( validation );
      if ( this.grantsNoAds( offer ) )
      {
        await OfferService.refresh();
        this.createContent();
      }
      else if ( OfferService.getOffer( offer.id )?.purchase_limit )
      {
        // The validate/claim response already updated the cached cycle state
        // (or the free-claim path refreshed the catalog), so a redraw is enough.
        this.createContent();
      }
      this.setTextAndRedraw( price_text, "OK" );

      this.onCoinsPurchased?.();

      window.setTimeout( () => this.setTextAndRedraw( price_text, previous_text ), 1000 );
    }
    catch ( e )
    {
      console.warn( '[Shop] purchase failed', e );
      this.setTextAndRedraw( price_text, previous_text || "Buy" );
      showPurchaseFailedTooltip( button, e );
      if ( !isPurchaseErrorReported( e ) )
        crashCapture( e, { phase: 'shop_purchase', offer_id: offer.id } );
    }
    finally
    {
      this.purchase_in_progress_offer_id = null;
    }
  }

  public name(): string
  {
    return "ShopScreen";
  }

  private initializeEventsController( canvas: HTMLCanvasElement ): void
  {
    if ( this.events_controller )
      this.events_controller.cleanup();

    this.events_controller = new BasicEventsController( canvas );

    this.events_controller.init(
      ( p: Vector2 ) => super.onTouchStart( p, performance.now() ),
      ( p: Vector2 ) => super.onTouchEnd( p, performance.now() ),
      ( p: Vector2 ) => super.onTouchMove( p, performance.now() )
    );
  }

  private reportShopClosed(): void
  {
    if ( this.shop_shown_at <= 0 ) return;
    trackFeatureEvent( "shop_closed", { duration_ms: Date.now() - this.shop_shown_at } );
    this.shop_shown_at = 0;
  }

  public destroy(): void
  {
    this.reportShopClosed();
    this.hideLockedOfferTooltip();
    hidePurchaseFailedTooltip();
    if ( this.offer_shop_opened_reported && !this.offer_shop_closed_reported )
    {
      this.offer_shop_closed_reported = true;
      void OfferTriggerController.report( 'shop_closed' );
    }
    if ( this.events_controller )
    {
      this.events_controller.cleanup();
      this.events_controller = null;
    }
  }


  protected override syncDomContent( _node: HTMLElement ): boolean { return true; }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();
    this.finishRender();

    if ( this.render_top_safe_area )
    {
      if ( this.dom_mode )
        SafeAreas.syncTopSafeAreaOverlayDom( this.getDomHost() );
      else
        SafeAreas.renderTopSafeAreaOverlay( this.ctx );
    }
  }

  public update( now: number ): void
  {
    this.updateBasic( now );
    this.syncActiveSectionToScroll();
    this.updateTimedOfferVisuals();

    if ( now >= this.next_coins_sync_at )
    {
      this.next_coins_sync_at = now + 250;
      this.syncCoins();
    }

  }
}
